import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    ActiveMilitaryPersonalInfoViewModel,
    ActiveMilitaryPersonalInfoResponse,
    hasFailedInstantMatch,
    getAvailableMilitaryStatuses,
    updateMilitaryViewModel,
    logger,
    VerificationStepsEnum,
    resetRefs,
    RewardsRemainingComponent,
    CountryComponentWrapper,
    getStatusLabel,
    MilitaryStatusDefaultMessagesEnum,
    FormSelectChoice,
    updateFieldValidationErrorsByFieldId,
    Organization,
    PhoneNumberComponent as PhoneNumber,
    updateViewModelOrganization,
    handleEmailOnKeyDown,
    MarketConsentWrapper,
    submitForm,
    isFormFilled,
    setRef,
    FormFooterComponent as FormFooter,
} from '@sheerid/jslib';

import { BranchOfServiceComponent as BranchOfService } from '../../FormFields/BranchOfServiceComponent';
import { MilitaryStatusComponent as MilitaryStatus } from '../../FormFields/MilitaryStatusComponent';
import { BirthDateComponent as BirthDate } from '../../FormFields/BirthDateComponent';
import { EmailComponent as Email } from '../../FormFields/EmailComponent';
import { FirstNameComponent as FirstName } from '../../FormFields/FirstNameComponent';
import { LastNameComponent as LastName } from '../../FormFields/LastNameComponent';

 interface StepActiveMilitaryPersonalInfoComponentProps {
     intl: any;
     verificationService: VerificationService;
 }

export const StepActiveMilitaryPersonalInfo = ({ intl, verificationService }: StepActiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as ActiveMilitaryPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;
    const verificationResponse = verificationService.verificationResponse as ActiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const { status } = (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel);

    const availableStatuses = getAvailableMilitaryStatuses(verificationService, intl);

    const updateActiveMilitaryViewModel = (key: keyof ActiveMilitaryPersonalInfoViewModel, value: any) => {
        verificationService.updateViewModel(updateMilitaryViewModel(key, value, verificationService, availableStatuses));
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            verificationService.previousVerificationResponse || verificationService.verificationResponse,
        );
    };

    if ((viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status)
         || (viewModel.status && verificationService.verificationResponse.currentStep === VerificationStepsEnum.collectMilitaryStatus)) {
        submitStep();
    }

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-active-military-personal-info" className="sid-l-container">
            <img className="sid-header__logo" src="https://assets-resources.sheerid.com/jcrew/img/j.crew_logo_2022.png" alt="" />
            { failedInstantMatch
                ? (
                    <div className="sid-header">
                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                        </div>

                        <div className="sid-header__subtitle-info sid-l-horz-center sid-header__subtitle--error">
                            <FormattedHTMLMessage
                                id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name and birthdate must match what is on your official school records."
                            />
                        </div>
                    </div>
                )
                : (
                    <div className="sid-header">
                        <div className="sid-header__subtitle-info sid-l-horz-center">
                            <FormattedHTMLMessage
                                id="step.personalInfo.subtitle"
                                defaultMessage="Complete the college student form below to confirm your eligibility."
                            />
                        </div>

                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student-Only Offer" />
                        </div>


                        <div className="sid-header__how-verifying-works sid-l-horz-center">
                            <RewardsRemainingComponent verificationService={verificationService} />
                        </div>
                    </div>
                )}

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="status"
            />
            <div className="sid-sub-header-all-fields-text">
                <FormattedHTMLMessage
                    id="requiredFields"
                />
            </div>
            {
                availableStatuses
                    ? (
                        <MilitaryStatus
                            value={{
                                value: viewModel.status,
                                label: getStatusLabel(intl, viewModel.status, MilitaryStatusDefaultMessagesEnum),
                            }}
                            isErrored={!!fieldValidationErrors.status}
                            options={availableStatuses}
                            onChange={
                                (status: FormSelectChoice) => {
                                    updateActiveMilitaryViewModel('status', status ? (status.value as string) : '');
                                    updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                                }
                            }
                        />
                    )
                    : null
            }

            {
                status === 'MILITARY_FAMILY' ? (
                    <div className="sid-field-notice" data-testid="family-note">
                        <FormattedHTMLMessage
                            id="militaryFamilyCardNote"
                            defaultMessage="Note: this offer is only available to military family members with a Dependent ID card."
                        />
                    </div>
                ) : null
            }

            <BranchOfService
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={{
                    value: (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).organization.id,
                    label: (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).organization.name,
                }}
                verificationService={verificationService}
                organizations={verificationService.orgList}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateActiveMilitaryViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                    placeholder={
                        intl.formatMessage({
                            id: 'firstName',
                            defaultMessage: 'First Name',
                        })
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateActiveMilitaryViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                    placeholder={
                        intl.formatMessage({
                            id: 'lastName',
                            defaultMessage: 'Last Name',
                        })
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage=""
                    />
                )}
                onChange={
                    (newValue: string) => {
                        updateActiveMilitaryViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                placeholder={
                    intl.formatMessage({
                        id: 'email',
                        defaultMessage: 'Email',
                    })
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled
                    ? (
                        <PhoneNumber
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            onChange={
                                (newValue: string) => {
                                    updateActiveMilitaryViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    )
                    : null
            }
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />
            <div className="sid-privacy-policy-container">
                <span>
                    <FormattedHTMLMessage
                        id="jcrewPrivacyPolicy"
                    />
                </span>
            </div>
            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectActiveMilitaryPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch
                                ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                                : <FormattedHTMLMessage id="verifyMyMilitaryStatus" defaultMessage="Verify My Military Status" />}
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="militaryInfoShared"
                />
                {
                    viewModel.countryChoice && viewModel.countryChoice.value !== 'US'
                        ? (
                            <div>
                                <FormattedHTMLMessage
                                    id="informationTransferredToUS"
                                    defaultMessage="Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                                />

                            </div>
                        )

                        : null
                }

                <div className="sid-last-footer">
                    <span className="sid-footer-one">
                        <FormattedHTMLMessage
                            id="lastFooter"
                        />
                    </span>

                    <p className="sid-footer-two">
                        <FormattedHTMLMessage
                            id="footerOne"
                        />
                        <FormattedHTMLMessage
                            id="footerTwo"
                        />
                    </p>
                </div>
            </FormFooter>
        </div>
    );
};

export const StepActiveMilitaryPersonalInfoComponent = injectIntl(StepActiveMilitaryPersonalInfo);
